import { useState } from 'react';
import { FiLogOut, FiUser, FiMoon, FiSun, FiMonitor, FiCopy, FiCheck, FiEye, FiEyeOff, FiShuffle } from 'react-icons/fi';
import ScreenContainer from '../components/ScreenContainer';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDisplayPrivacy } from '../contexts/DisplayPrivacyContext';
import { copyToClipboard } from '../utils/clipboard';
import CustomToast from '../components/CustomToast';

function SettingsScreen() {
  const { logout, user } = useAuth();
  const { mode, resolvedTheme, setMode } = useTheme();
  const { randomizeAmounts, toggleRandomizeAmounts } = useDisplayPrivacy();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [showSecureEmailId, setShowSecureEmailId] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout', error);
      setToastMessage(`Failed to logout: ${error instanceof Error ? error.message : String(error)}`);
      window.setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const handleCopyEmailId = async () => {
    if (!user?.secureEmailId) {
      return;
    }
    try {
      await copyToClipboard(user.secureEmailId);
      setCopyState('copied');
    } catch (error) {
      console.error('Failed to copy email ID', error);
      setCopyState('error');
    } finally {
      window.setTimeout(() => setCopyState('idle'), 1200);
    }
  };

  return (
    <ScreenContainer title="Settings">
      <div className="mx-auto w-full max-w-md">
        <div className="h-auto p-4 pt-2 space-y-4">

          {/* Account Details Section */}
          <div className="flex flex-col gap-4 bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-secondary rounded-lg">
                <FiUser className="text-foreground" size={24} />
              </div>
              <div>
                <p className="text-sm text-foreground">
                  {user?.fullName}
                </p>
                <p className="text-sm text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  AutoFi Email ID
                </p>
                <div className="inline-flex items-center gap-2">
                  <button
                    onClick={() => setShowSecureEmailId((prev) => !prev)}
                    disabled={!user?.secureEmailId}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-muted cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={showSecureEmailId ? 'Hide AutoFi email ID' : 'Show AutoFi email ID'}
                  >
                    {showSecureEmailId ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    {showSecureEmailId ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={handleCopyEmailId}
                    disabled={!user?.secureEmailId}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-muted cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Copy AutoFi email ID"
                  >
                    {copyState === 'copied' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                    {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Retry' : 'Copy'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-foreground font-mono break-all select-text cursor-text">
                {user?.secureEmailId ? (showSecureEmailId ? user.secureEmailId : <span><span className="text-muted-foreground/70">****</span>@autofi.in</span>) : '-'}
              </p>
            </div>
          </div>

          {/* Appearance Section */}
          <div className="flex flex-col gap-4 bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-secondary rounded-lg">
                {resolvedTheme === 'dark' ? (
                  <FiMoon className="text-foreground" size={24} />
                ) : (
                  <FiSun className="text-foreground" size={24} />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Appearance</p>
                <p className="text-sm text-muted-foreground">
                  Theme: {mode === 'system' ? `System (${resolvedTheme})` : mode}
                </p>
              </div>
            </div>
            <div className="flex flex-row flex-wrap gap-2">
              <button
                onClick={() => setMode('light')}
                className={`flex flex-grow items-center justify-center gap-2 rounded-lg border px-2 py-2 text-sm font-medium transition-colors ${mode === 'light'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                  }`}
              >
                <FiSun size={16} />
                Light
              </button>
              <button
                onClick={() => setMode('dark')}
                className={`flex flex-grow items-center justify-center gap-2 rounded-lg border px-2 py-2 text-sm font-medium transition-colors ${mode === 'dark'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                  }`}
              >
                <FiMoon size={16} />
                Dark
              </button>
              <button
                onClick={() => setMode('system')}
                className={`flex flex-grow items-center justify-center gap-2 rounded-lg border px-2 py-2 text-sm font-medium transition-colors ${mode === 'system'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                  }`}
              >
                <FiMonitor size={16} />
                System
              </button>
            </div>
          </div>

          {/* Privacy Section */}
          <div className="flex flex-col gap-4 bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-secondary rounded-lg">
                <FiShuffle className="text-foreground" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Randomize balances and transaction amounts for safe demos.
                </p>
              </div>
            </div>

            <button
              onClick={toggleRandomizeAmounts}
              role="switch"
              aria-checked={randomizeAmounts}
              className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-3 text-left hover:bg-muted/70 transition-colors"
            >
              <div className="pr-3">
                <p className="text-sm font-medium text-foreground">Show Fake Data</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {randomizeAmounts ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div
                className={`h-6 w-11 rounded-full transition-colors ${randomizeAmounts ? 'bg-primary' : 'bg-muted-foreground/40'}`}
              >
                <div
                  className={`h-5 w-5 rounded-full bg-white shadow-sm mt-0.5 transition-transform ${randomizeAmounts ? 'translate-x-5' : 'translate-x-0.5'}`}
                />
              </div>
            </button>
          </div>

          <div className="pt-2 flex justify-center">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium"
            >
              <FiLogOut size={18} />
              Log out
            </button>
          </div>

        </div>
      </div>
      <CustomToast message={toastMessage} isVisible={!!toastMessage} />
    </ScreenContainer>
  );
}

export default SettingsScreen; 
